"use client"

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react'

interface Customer {
  _id: string
  name: string
  email?: string
  phone?: string
  company?: string
  address?: string
  city?: string
  state?: string
  zipCode?: string
  country?: string
  status: "active" | "inactive" | "prospect"
  created_at: string
}

interface CustomerContextType {
  customers: Customer[]
  loading: boolean
  error: string | null
  refreshCustomers: () => Promise<void>
  invalidateCustomers: () => void
}

const CustomerContext = createContext<CustomerContextType | undefined>(undefined)

export function useCustomers() {
  const context = useContext(CustomerContext)
  if (context === undefined) {
    throw new Error('useCustomers must be used within a CustomerProvider')
  }
  return context
}

interface CustomerProviderProps {
  children: ReactNode
}

export function CustomerProvider({ children }: CustomerProviderProps) {
  const [customers, setCustomers] = useState<Customer[]>([])
  const [loading, setLoading] = useState(false) // Changed from true to false
  const [error, setError] = useState<string | null>(null)
  const [lastFetch, setLastFetch] = useState<number>(0)

  const fetchCustomers = async () => {
    try {
      setLoading(true)
      setError(null)
      console.log("Fetching customers...")

      const response = await fetch("/api/customers", {
        method: "GET",
        credentials: "include",
        cache: "no-store",
        headers: {
          "Content-Type": "application/json"
        }
      })

      console.log("Customers response status:", response.status)

      if (!response.ok) {
        const errorText = await response.text()
        console.error("Failed to fetch customers response:", errorText)
        // Don't show error for auth issues (401/403), as auth is handled separately
        if (response.status === 401 || response.status === 403) {
          setCustomers([])
          setLoading(false)
          return
        }
        throw new Error(`Failed to fetch customers: ${response.status}`)
      }

      const data = await response.json()
      console.log("Fetched customers:", data)
      setCustomers(Array.isArray(data) ? data : [])
      setLastFetch(Date.now())
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Failed to fetch customers'
      setError(errMsg)
      console.error("Failed to fetch customers:", err)
      setCustomers([])
    } finally {
      setLoading(false)
    }
  }

  const refreshCustomers = useCallback(async () => {
    await fetchCustomers()
  }, [])

  const invalidateCustomers = useCallback(() => {
    setLastFetch(0) // Force next access to refetch
    // Immediately refetch when invalidated
    fetchCustomers()
  }, [])

  // Don't auto-fetch on mount - let components trigger it after auth
  // This prevents fetching before authentication is established
  // Components should call refreshCustomers() after successful auth check

  // Listen for customer updates from other tabs/windows
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'customer_updated' && e.newValue) {
        invalidateCustomers()
      }
    }

    const handleCustomerUpdate = () => {
      invalidateCustomers()
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('customerUpdated', handleCustomerUpdate)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('customerUpdated', handleCustomerUpdate)
    }
  }, [])

  const value: CustomerContextType = {
    customers,
    loading,
    error,
    refreshCustomers,
    invalidateCustomers
  }

  return (
    <CustomerContext.Provider value={value}>
      {children}
    </CustomerContext.Provider>
  )
}

// Utility function to notify other components of customer updates
export function notifyCustomerUpdate() {
  // Notify current tab
  window.dispatchEvent(new CustomEvent('customerUpdated'))

  // Notify other tabs/windows
  localStorage.setItem('customer_updated', Date.now().toString())
  // Clear it immediately so the event fires
  setTimeout(() => localStorage.removeItem('customer_updated'), 100)
}